import React, { useEffect, useRef, useState } from "react";
import EATextField from "../TextField/EATextField";
import "./EABirthDateInput.scss";

interface RHFField {
  onChange: (date: Date | null) => void;
  value: Date | null | string;
  name: string;
}

interface IProps {
  label: string;
  name: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  field?: RHFField;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
}

const EABirthDateInput: React.FC<IProps> = ({
  label,
  name,
  value,
  onChange,
  field,
  disabled = false,
  error = false,
  helperText = "",
  fullWidth = false,
}) => {
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  const initialValue = useRef(field?.value ?? value);

  useEffect(() => {
    const initVal = initialValue.current;
    if (initVal instanceof Date && !isNaN(initVal.getTime())) {
      setDay(String(initVal.getDate()).padStart(2, "0"));
      setMonth(String(initVal.getMonth() + 1).padStart(2, "0"));
      setYear(String(initVal.getFullYear()));
    }
  }, []);

  const emitChange = (d: string, m: string, y: string) => {
    const notify = field?.onChange ?? onChange;
    if (!notify) return;

    if (!d || !m || !y) {
      notify(null);
      return;
    }

    const dayNum = parseInt(d, 10);
    const monthNum = parseInt(m, 10);
    const yearNum = parseInt(y, 10);

    // Clearly out-of-range values → emit invalid date immediately
    if ((d.length === 2 && (dayNum < 1 || dayNum > 31)) || (m.length === 2 && (monthNum < 1 || monthNum > 12))) {
      notify(new Date(NaN));
      return;
    }

    // Year not yet complete → treat as empty
    if (y.length < 4) {
      notify(null);
      return;
    }

    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (
      date.getFullYear() === yearNum &&
      date.getMonth() === monthNum - 1 &&
      date.getDate() === dayNum
    ) {
      notify(date);
    } else {
      notify(new Date(NaN));
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
    setDay(val);
    emitChange(val, month, year);
    if (val.length === 2) document.getElementById(`${name}-month`)?.focus();
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 2);
    setMonth(val);
    emitChange(day, val, year);
    if (val.length === 2) document.getElementById(`${name}-year`)?.focus();
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
    setYear(val);
    emitChange(day, month, val);
  };

  return (
    <div className={`birth-date-input ${fullWidth ? "full-width" : ""}`}>
      <label className={`birth-date-input__label ${disabled ? "disabled" : ""}`}>
        {label}
      </label>
      <div className="birth-date-input__fields">
        <div className="birth-date-input__field">
          <EATextField
            name={`${name}-day`}
            placeholder="DD"
            value={day}
            onChange={handleDayChange}
            disabled={disabled}
            error={error}
            helperText="Dzień"
            fullWidth
            register={{ inputMode: "numeric", maxLength: 2 }}
          />
        </div>
        <div className="birth-date-input__field">
          <EATextField
            name={`${name}-month`}
            placeholder="MM"
            value={month}
            onChange={handleMonthChange}
            disabled={disabled}
            error={error}
            helperText="Miesiąc"
            fullWidth
            register={{ inputMode: "numeric", maxLength: 2 }}
          />
        </div>
        <div className="birth-date-input__field">
          <EATextField
            name={`${name}-year`}
            placeholder="RRRR"
            value={year}
            onChange={handleYearChange}
            disabled={disabled}
            error={error}
            helperText="Rok"
            fullWidth
            register={{ inputMode: "numeric", maxLength: 4 }}
          />
        </div>
      </div>
      {helperText && (
        <p className="birth-date-input__helper-text birth-date-input__helper-text--error">
          {helperText}
        </p>
      )}
    </div>
  );
};

export default EABirthDateInput;
